import { useEffect, useRef, useState } from "react";
import { observer } from "@legendapp/state/react";
import { useNavigate } from "react-router-dom";
import { WordsBadge, colors } from "@/ui";
import { useDictionaryStats } from "@/hooks/useDictionaryStats";
import { CompletionCelebration } from "@/features/tutorial";
import {
  useFeedStreak,
  showCelebration$,
  showStreakScreen$,
  showGoalPicker$,
} from "../../features/streak/useFeedStreak";
import { FeedStreakScreen } from "../../features/streak/FeedStreakScreen";
import { ListeningGoalPicker } from "../../features/streak/ListeningGoalPicker";

export const TOP_BAR_HEIGHT = "52px";

/** Bar metrics — mirror iOS ListeningBar (HEIGHT 14, circles 14*2.2 ≈ 31). */
const BAR_HEIGHT = 14;
const CIRCLE_SIZE = Math.round(BAR_HEIGHT * 2.2); // ≈ 31

interface ListeningBarProps {
  currentListeningTime: number;
  targetListeningTime: number;
  onPress: () => void;
}

/**
 * Listening-goal bar — web port of iOS
 * `client-native/src/ui/atoms/indicator/ListeningBar.tsx`.
 *
 * Track #F2F8FF, fill #0c92f1 with a top highlight strip, a MIDWAY circle at 50%
 * showing the half-goal minutes, and a GOAL trophy circle at the right end. The
 * goal is server-driven (`userSettings.dailyListeningGoalMinutes`, default 5)
 * and so is the listening time (`getDailyListening`, via useFeedStreak — iOS
 * FeedHeaderProgress). Tapping the bar opens the daily-goal picker.
 */
const ListeningBar = observer(function ListeningBar({
  currentListeningTime,
  targetListeningTime,
  onPress,
}: ListeningBarProps) {
  const goalMinutes = Math.round(targetListeningTime / 60);
  const halfGoalMinutes = Math.round(goalMinutes / 2);

  const progress = Math.min(currentListeningTime / targetListeningTime, 1);
  const pct = progress * 100;
  const done = progress >= 1;
  const remainingSeconds = Math.max(targetListeningTime - currentListeningTime, 0);
  const minsLeft = Math.ceil(remainingSeconds / 60);
  const crossedMidway = progress >= 0.5;

  const [celebrating, setCelebrating] = useState(false);
  const prevDone = useRef(done);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (done && !prevDone.current) {
      setCelebrating(true);
      containerRef.current?.animate(
        [
          { transform: "scale(1)", offset: 0 },
          { transform: "scale(1.08)", offset: 0.25 },
          { transform: "scale(1.12)", offset: 0.45 },
          { transform: "scale(1.10)", offset: 0.6 },
          { transform: "scale(1.05)", offset: 0.8 },
          { transform: "scale(1)", offset: 1 },
        ],
        { duration: 900, easing: "cubic-bezier(0.34, 1.56, 0.64, 1)" },
      );
      setTimeout(() => setCelebrating(false), 900);
    }
    prevDone.current = done;
  }, [done]);

  const trackBg = colors.lightBlueTranslucent; // #F2F8FF
  const fillBg = celebrating ? colors.green : colors.blue; // #0c92f1
  const goalActive = done;

  return (
    <div
      ref={containerRef}
      className="flex-1 flex items-center relative cursor-pointer"
      onClick={onPress}
      role="button"
      aria-label="Daily listening goal"
    >
      <div
        className="flex-1 relative rounded-full overflow-visible"
        style={{ height: BAR_HEIGHT }}
      >
        {/* Track */}
        <div
          className="absolute inset-0 rounded-full overflow-hidden"
          style={{ backgroundColor: trackBg }}
        >
          {/* Fill */}
          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{
              width: pct > 0 ? `max(${pct}%, ${BAR_HEIGHT}px)` : "0%",
              backgroundColor: fillBg,
              transition: "width 0.3s ease-out, background-color 0.4s ease",
            }}
          >
            {/* Top highlight strip (rgba(255,255,255,0.9) @ 0.35 opacity) */}
            <div
              className="absolute rounded-full"
              style={{
                top: 2.5,
                left: 5,
                right: 5,
                height: Math.max(2, Math.round(BAR_HEIGHT * 0.2)),
                backgroundColor: "rgba(255,255,255,0.9)",
                opacity: 0.35,
              }}
            />
          </div>
          {/* "X mins left" — only while <=75% complete */}
          {progress <= 0.75 && (
            <span
              className="absolute inset-0 flex items-center justify-end pr-9 text-[12px] font-medium whitespace-nowrap"
              style={{ color: colors.muted }}
            >
              {minsLeft === 1 ? "1 min left" : `${minsLeft} mins left`}
            </span>
          )}
        </div>

        {/* Midway circle (half-goal minutes) at 50% */}
        <div
          className="absolute z-10 rounded-full flex items-center justify-center text-[12px] font-bold"
          style={{
            width: CIRCLE_SIZE,
            height: CIRCLE_SIZE,
            left: `calc(50% - ${CIRCLE_SIZE / 2}px)`,
            top: -Math.round(BAR_HEIGHT * 0.7),
            backgroundColor: crossedMidway ? "#3daaff" : colors.white,
            color: crossedMidway ? colors.white : colors.black,
            boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
          }}
        >
          {halfGoalMinutes}
        </div>
      </div>

      {/* Goal trophy circle at the right end */}
      <div
        className="shrink-0 relative z-10 rounded-full flex items-center justify-center"
        style={{
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          marginLeft: -CIRCLE_SIZE / 2,
          marginTop: -Math.round(BAR_HEIGHT * 0.7),
          backgroundColor: goalActive ? colors.blue : colors.white,
          boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
        }}
      >
        <span className="text-base leading-none">🏆</span>
      </div>
    </div>
  );
});

export const TopBar = observer(function TopBar() {
  const navigate = useNavigate();
  const { knownWordCount, totalWordCount } = useDictionaryStats();
  // iOS WordsBadge: below 100 total words show the total ("X words"); at/above
  // the threshold switch to known-only ("X words known"). Mirrors
  // useDictionaryStats badgeCount/showKnownBadge on iOS.
  const showKnown = totalWordCount >= 100;

  // Daily-goal streak flow (iOS FeedHeaderProgress): server-driven listening
  // bar, streak claim → celebration → streak screen, tappable goal picker.
  const streak = useFeedStreak();

  return (
    <>
      <div
        className="flex items-center gap-3 px-3 pt-4 pb-2 bg-black/60 backdrop-blur-sm"
        style={{ height: TOP_BAR_HEIGHT }}
      >
        <ListeningBar
          currentListeningTime={streak.currentListeningTime}
          targetListeningTime={streak.targetListeningTime}
          onPress={streak.openGoalPicker}
        />
        <div className="shrink-0">
          {/* iOS FeedCustomHeader renders <WordsBadge /> with no variant, i.e. the
              "short" default ("X known" / "X words"). Keep it explicit so the
              atom's default change can't silently flip this surface. */}
          <WordsBadge
            count={showKnown ? knownWordCount : totalWordCount}
            known={showKnown}
            variant="short"
            onClick={() => navigate("/home/dictionary")}
          />
        </div>
      </div>

      {/* Streak-flow overlays — rendered by exactly one mounted TopBar (the
          carousel keeps previous/next panels alive, each with its own bar). */}
      {streak.isOverlayOwner && (
        <>
          <CompletionCelebration
            isVisible={showCelebration$.get()}
            onDismiss={streak.handleCelebrationDismiss}
          />
          {showStreakScreen$.get() && (
            <FeedStreakScreen onContinue={streak.handleStreakContinue} />
          )}
          {showGoalPicker$.get() && (
            <ListeningGoalPicker
              onGoalChange={streak.handleGoalChange}
              onClose={streak.closeGoalPicker}
            />
          )}
        </>
      )}
    </>
  );
});
